import { useEffect, useState } from "react"
import { motion } from "motion/react"
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle,
  Send01,
  Stars02,
} from "@untitledui/icons"
import { useNavigate } from "react-router"
import { toast } from "sonner"
import { ApiError } from "@/lib/api/client"
import type { TeamSettingsMetadata } from "@/lib/api/types"
import { useTeam } from "@/lib/api/use-teams"
import { withGoalsRewardsDefaults } from "@/lib/dashboard/defaults"
import {
  Field,
  PrimaryButton,
  onboardingInputClass,
} from "@/components/onboarding/onboarding-shell"
import { cx } from "@/lib/utils/cx"
import { formatPhone, isValidPhone } from "@/lib/utils/phone"
import { QE_SEND_COPY } from "./copy"
import type { QEPhoto, VinDecodedVehicle } from "./types"
import {
  DAMAGE_TYPE_LABEL,
  SEVERITY_LABEL,
  type DamageTag,
} from "./damage-tag/panels"
import type {
  QuickEstimateActions,
  QuickEstimateCustomer,
} from "./use-quick-estimate-state"
import {
  readRecentCustomer,
  recordRecentCustomer,
} from "./recent-customer"
import { submitQuickEstimate } from "./submit"
import type { UseQECapture } from "./use-qe-capture"

// Quick Estimate 3 — Step 4 (Send).
//
// The previous steps already handled review: photos (quality) on
// step 2, tags (sides) on step 3. This step's only job is to
// collect the customer (the one thing the prior steps didn't gather)
// and ship.
//
// What that means for the layout:
//   • No big vehicle card / photo grid recap. Just a tight inline
//     row with vehicle + photo count + an Edit shortcut back to
//     tags step for last-minute fixes.
//   • Customer is the focal block — expanded by default, no toggle,
//     side-by-side fields on desktop.
//   • Notes inline (small textarea, optional).
//   • Big Send CTA.

interface StepSendProps {
  /** Source-of-truth VIN. State always has it after Vehicle step
   *  succeeds; vehicle.vin is a duplicate that may not be populated
   *  on partial decode failures, so prefer this. */
  vin: string
  vehicle: VinDecodedVehicle | null
  photos: QEPhoto[]
  /** Pre-photo damage tags (quick-estimate only; empty in full-estimate). */
  damageTags?: DamageTag[]
  notes: string
  customer: QuickEstimateCustomer
  actions: QuickEstimateActions
  capture: UseQECapture
  /** Required when `mode="submit"` (QE3 terminal step). */
  teamId?: number | string
  onBack: () => void
  /** "submit" (default, QE3): runs the submit pipeline + success takeover.
   *  "continue" (full-estimate): validates the form, then calls onContinue
   *  so the parent wizard advances to the next step. */
  mode?: "submit" | "continue"
  /** Called when mode="continue" and the form passes validation. */
  onContinue?: () => void
  /** Overrides the default eyebrow label so the step can sit at a
   *  different position in different wizards (4 of 4 vs 4 of 8). */
  eyebrowLabel?: string
  /** Override CTA label/icon for "continue" mode. */
  continueCtaLabel?: string
}

type SubmitState =
  | { kind: "idle" }
  | { kind: "sending" }
  | {
      kind: "success"
      estimateId: number
      photosAttached: number
      totalPhotos: number
    }
  | { kind: "error"; message: string }

export function StepSend({
  vin,
  vehicle,
  photos,
  damageTags = [],
  notes,
  customer,
  actions,
  teamId,
  onBack,
  mode = "submit",
  onContinue,
  eyebrowLabel,
  continueCtaLabel,
}: StepSendProps) {
  const navigate = useNavigate()
  const [submitState, setSubmitState] = useState<SubmitState>({ kind: "idle" })
  const [phoneError, setPhoneError] = useState<string | null>(null)
  const [recent, setRecent] = useState<{ name: string; phone: string } | null>(
    null,
  )

  const submittablePhotos = photos.filter(
    (p) => p.uploadedUrl.length > 0 || p.sendWithoutAiTag,
  )
  const noSubmittablePhotos = submittablePhotos.length === 0

  // Per-scan / per-paid rates for the success beat, resolved the same way
  // the SA dashboard hero does. Advisor count only shapes goal targets,
  // not reward amounts, so 1 is fine. Zeroed until the team loads so we
  // never flash platform-default dollars a manager may have turned off.
  const { data: team } = useTeam(teamId ?? null)
  const meta = (team?.metadata ?? {}) as TeamSettingsMetadata
  const goals = withGoalsRewardsDefaults(meta.goalsRewards, meta.serviceLane, 1)
  const perScan =
    team && goals.rewards?.perEstimateSubmitted?.monetary?.enabled
      ? goals.rewards.perEstimateSubmitted.monetary.amount ?? 0
      : 0
  const perPaid =
    team && goals.rewards?.perEstimateCompleted?.monetary?.enabled
      ? goals.rewards.perEstimateCompleted.monetary.amount ?? 0
      : 0

  useEffect(() => {
    if (customer.name || customer.phone) return
    const cached = readRecentCustomer()
    if (cached && (cached.name || cached.phone)) {
      setRecent({ name: cached.name, phone: cached.phone })
    }
  }, [customer.name, customer.phone])

  // Phone is required by the BE customer DTO (ValidateIf forces at
  // least one of email/phone, and the service line doesn't collect
  // email). Block the submit if empty or malformed.
  const trimmedPhone = customer.phone.trim()
  const phoneOk = trimmedPhone.length > 0 && isValidPhone(trimmedPhone)

  const handleSubmit = async () => {
    if (!phoneOk) {
      setPhoneError(
        trimmedPhone.length === 0
          ? "Phone number is required"
          : "Enter a valid phone number",
      )
      return
    }
    if (noSubmittablePhotos) return

    // continue mode: parent wizard owns submission. We just record the
    // recent customer so it's prefilled next time, then advance.
    if (mode === "continue") {
      recordRecentCustomer(customer.name, customer.phone)
      onContinue?.()
      return
    }

    if (teamId == null) {
      setSubmitState({ kind: "error", message: "Missing team — can't submit" })
      return
    }
    // Guard: vehicle.vin is the FE-side source of truth at submit time;
    // if it's empty the user skipped the Vehicle step somehow. BE rejects
    // empty VIN so fail fast with a clearer message than a 400 toast.
    const finalVin = (vin || vehicle?.vin || "").trim()
    if (finalVin.length === 0) {
      setSubmitState({
        kind: "error",
        message: "VIN is missing — go back and add a vehicle.",
      })
      return
    }
    try {
      navigator.vibrate?.([10, 30, 10])
    } catch {
      // ignore
    }
    setSubmitState({ kind: "sending" })
    try {
      const result = await submitQuickEstimate({
        teamId,
        vin: finalVin,
        vehicle,
        photos: submittablePhotos,
        damageTags,
        notes: notes.trim(),
        customer,
      })
      recordRecentCustomer(customer.name, customer.phone)
      // NOTE: state reset is deferred to the takeover dismissal
      // callbacks below. Resetting here flips state.step back to 0,
      // which makes the wizard parent unmount StepSend before the
      // success takeover gets to render — the user would see step 1
      // (Vehicle) instead of the "Estimate queued" screen.
      setSubmitState({
        kind: "success",
        estimateId: result.estimateId,
        photosAttached: result.photosAttached,
        totalPhotos: submittablePhotos.length,
      })
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Couldn't send the estimate"
      setSubmitState({ kind: "error", message: msg })
      toast.error(msg)
    }
  }

  if (submitState.kind === "success") {
    return (
      <SuccessTakeover
        vehicle={vehicle}
        photosAttached={submitState.photosAttached}
        totalPhotos={submitState.totalPhotos}
        perScan={perScan}
        perPaid={perPaid}
        onPrimary={() => {
          actions.reset()
          navigate(`/estimates/${submitState.estimateId}`)
        }}
        onSecondary={() => {
          // Stay on /estimates/quick. Same-route navigate() does not
          // remount the wizard, so we have to reset state ourselves
          // to send the user back to step 0 with a clean slate.
          actions.reset()
          setSubmitState({ kind: "idle" })
        }}
      />
    )
  }

  return (
    <div className="space-y-6 pb-36 sm:pb-0">
      <header>
        <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-emerald-600 mb-3">
          {eyebrowLabel ?? QE_SEND_COPY.eyebrow}
        </p>
        <h2 className="text-2xl md:text-[28px] font-bold text-zinc-900 tracking-tight leading-tight">
          {QE_SEND_COPY.title}
        </h2>
        <p className="mt-1.5 text-[14px] text-zinc-500 leading-relaxed">
          {QE_SEND_COPY.subtitle}
        </p>
      </header>

      <RecapRow
        vehicle={vehicle}
        photoCount={submittablePhotos.length}
        onEdit={onBack}
      />

      {damageTags.length > 0 && <DamageRecap tags={damageTags} />}

      {noSubmittablePhotos && (
        <div className="rounded-xl border border-amber-200 bg-amber-50/60 px-4 py-3 flex items-start gap-2.5">
          <AlertCircle
            className="size-4 text-amber-600 shrink-0 mt-0.5"
            aria-hidden
          />
          <p className="text-[13px] font-medium text-amber-900 leading-snug">
            {QE_SEND_COPY.blockNoPhotos}
          </p>
        </div>
      )}

      <CustomerBlock
        customer={customer}
        recent={recent}
        phoneError={phoneError}
        onSet={(c) => actions.setCustomer(c)}
        onUseRecent={() => {
          if (!recent) return
          actions.setCustomer({ name: recent.name, phone: recent.phone })
          setRecent(null)
        }}
        onPhoneErrorClear={() => setPhoneError(null)}
      />

      <NotesField value={notes} onChange={(v) => actions.setNotes(v)} />

      {/* CTA bar */}
      <div
        className={cx(
          "space-y-2",
          "fixed inset-x-0 bottom-0 z-30",
          "px-4 pt-3 pb-[max(env(safe-area-inset-bottom),12px)]",
          "bg-gradient-to-t from-white via-white/95 to-white/0",
          "sm:static sm:px-0 sm:pt-2 sm:pb-0 sm:bg-transparent",
        )}
      >
        <PrimaryButton
          loading={submitState.kind === "sending"}
          onClick={handleSubmit}
          type="button"
          size="wide"
          disabled={noSubmittablePhotos || submitState.kind === "sending"}
        >
          <Send01 className="size-4" aria-hidden />
          {mode === "continue"
            ? continueCtaLabel ?? "Continue"
            : submitState.kind === "sending"
              ? QE_SEND_COPY.submitting
              : QE_SEND_COPY.submit}
        </PrimaryButton>
        <button
          type="button"
          onClick={onBack}
          className="w-full h-10 inline-flex items-center justify-center gap-1.5 text-[12.5px] font-medium text-zinc-500 hover:text-zinc-900 transition-colors duration-150"
        >
          <ArrowLeft className="size-3.5" aria-hidden />
          {QE_SEND_COPY.back}
        </button>
      </div>
    </div>
  )
}

/* ── Compact recap row ────────────────────────────────────────── */

function RecapRow({
  vehicle,
  photoCount,
  onEdit,
}: {
  vehicle: VinDecodedVehicle | null
  photoCount: number
  onEdit: () => void
}) {
  const noPhotos = photoCount === 0
  const vehicleLine =
    [vehicle?.year, vehicle?.make, vehicle?.model].filter(Boolean).join(" ") ||
    "Vehicle"
  const trailingVin = vehicle?.vin?.slice(-6)

  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-3 flex items-center gap-3">
      {vehicle?.colorHex ? (
        <div
          className="size-10 rounded-xl shrink-0 shadow-sm border border-zinc-200/70"
          style={{ background: vehicle.colorHex }}
          aria-hidden
        />
      ) : (
        <div
          className="size-10 rounded-xl shrink-0 bg-zinc-100 border border-zinc-200/70"
          aria-hidden
        />
      )}
      <div className="flex-1 min-w-0">
        <p className="text-[13.5px] font-semibold text-zinc-900 leading-tight truncate">
          {vehicleLine}
        </p>
        <div className="mt-0.5 flex items-center gap-1.5 text-[11.5px] leading-tight text-zinc-500">
          {trailingVin && (
            <>
              <span className="font-mono uppercase tracking-wide">
                {trailingVin}
              </span>
              <span className="text-zinc-300">·</span>
            </>
          )}
          {noPhotos ? (
            <>
              <AlertCircle className="size-3 text-amber-500" aria-hidden />
              <span className="text-amber-700 font-medium">
                {QE_SEND_COPY.recapNoPhotos}
              </span>
            </>
          ) : (
            <>
              <CheckCircle
                className="size-3 text-emerald-500"
                strokeWidth={2.4}
                aria-hidden
              />
              <span className="text-zinc-600 font-medium">
                {QE_SEND_COPY.recapPhotoCount(photoCount)}
              </span>
            </>
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={onEdit}
        className="text-[11.5px] font-bold uppercase tracking-wider text-emerald-700 hover:text-emerald-800 shrink-0 px-2.5 h-9 rounded-lg hover:bg-emerald-50 transition-colors duration-150"
      >
        {QE_SEND_COPY.recapEdit}
      </button>
    </div>
  )
}

/* ── Tagged-damage recap (quick-estimate) ─────────────────────── */

function DamageRecap({ tags }: { tags: DamageTag[] }) {
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-3">
      <p className="mb-2 text-[10.5px] font-bold uppercase tracking-[0.14em] text-zinc-400">
        Tagged damage · {tags.length}
      </p>
      <div className="flex flex-wrap gap-1.5">
        {tags.map((t) => (
          <span
            key={t.panelId}
            className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-[12px] font-semibold text-emerald-800"
          >
            {t.label}
            <span className="font-normal text-emerald-600/80">
              · {SEVERITY_LABEL[t.severity]} {DAMAGE_TYPE_LABEL[t.damageType]}
            </span>
          </span>
        ))}
      </div>
    </div>
  )
}

/* ── Customer focal block ─────────────────────────────────────── */

function CustomerBlock({
  customer,
  recent,
  phoneError,
  onSet,
  onUseRecent,
  onPhoneErrorClear,
}: {
  customer: QuickEstimateCustomer
  recent: { name: string; phone: string } | null
  phoneError: string | null
  onSet: (c: Partial<QuickEstimateCustomer>) => void
  onUseRecent: () => void
  onPhoneErrorClear: () => void
}) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="text-[15px] font-bold text-zinc-900 tracking-tight">
          {QE_SEND_COPY.customerHeading}
        </h3>
        <span className="text-[10.5px] font-bold uppercase tracking-wider text-zinc-400">
          {QE_SEND_COPY.customerOptional}
        </span>
      </div>

      {recent && !customer.name && !customer.phone && (
        <button
          type="button"
          onClick={onUseRecent}
          className="w-full mb-3 flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl border border-emerald-200 bg-emerald-50/40 hover:bg-emerald-50 transition-colors duration-150 text-left"
        >
          <div className="min-w-0 flex items-center gap-2.5">
            <Stars02
              className="size-4 text-emerald-700 shrink-0"
              aria-hidden
            />
            <div className="min-w-0">
              <p className="text-[12.5px] font-semibold text-zinc-900 truncate">
                {recent.name || "—"}
                {recent.phone && (
                  <span className="text-zinc-500 font-normal">
                    {" "}
                    · {formatPhone(recent.phone)}
                  </span>
                )}
              </p>
              <p className="text-[10.5px] text-emerald-700/80 mt-0.5 leading-tight">
                Use last customer
              </p>
            </div>
          </div>
          <span className="text-[11px] font-bold text-emerald-700 uppercase tracking-wider shrink-0">
            Use
          </span>
        </button>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label={QE_SEND_COPY.customerNameLabel}>
          <input
            value={customer.name}
            onChange={(e) => onSet({ name: e.target.value })}
            placeholder="Jane Doe"
            maxLength={120}
            autoComplete="name"
            className={onboardingInputClass}
          />
        </Field>
        <Field
          label={QE_SEND_COPY.customerPhoneLabel}
          required
          error={phoneError ?? undefined}
        >
          <input
            type="tel"
            inputMode="tel"
            value={customer.phone}
            onChange={(e) => {
              onSet({ phone: formatPhone(e.target.value) })
              if (phoneError) onPhoneErrorClear()
            }}
            placeholder="(555) 123-4567"
            maxLength={20}
            autoComplete="tel"
            className={onboardingInputClass}
          />
        </Field>
      </div>
    </div>
  )
}

/* ── Inline notes field ───────────────────────────────────────── */

function NotesField({
  value,
  onChange,
}: {
  value: string
  onChange: (v: string) => void
}) {
  return (
    <div>
      <label className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-zinc-400 mb-1.5 block">
        {QE_SEND_COPY.notesLabel}
      </label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={QE_SEND_COPY.notesPlaceholder}
        rows={2}
        maxLength={1000}
        className={cx(
          onboardingInputClass,
          "h-auto py-2.5 resize-none leading-relaxed",
        )}
      />
    </div>
  )
}

/* ── Success takeover ─────────────────────────────────────────── */

function SuccessTakeover({
  vehicle,
  photosAttached,
  totalPhotos,
  perScan,
  perPaid,
  onPrimary,
  onSecondary,
}: {
  vehicle: VinDecodedVehicle | null
  photosAttached: number
  totalPhotos: number
  perScan: number
  perPaid: number
  onPrimary: () => void
  onSecondary: () => void
}) {
  const partial = photosAttached < totalPhotos
  // Wallet-first beat: what this save just paid, anchored to the moment
  // of action (same framing as the dashboard hero's subtitle).
  const earningsLine =
    perScan > 0 && perPaid > 0
      ? `+$${perScan} earned · $${perPaid} more when paid`
      : perScan > 0
        ? `+$${perScan} earned`
        : perPaid > 0
          ? `+$${perPaid} when paid`
          : null
  const subtitle =
    photosAttached === 0
      ? "Estimate created — re-attach photos from the estimate page."
      : photosAttached === 1
        ? QE_SEND_COPY.successSubtitleOne
        : QE_SEND_COPY.successSubtitleMany(photosAttached)

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ type: "spring", stiffness: 260, damping: 28 }}
      className="text-center py-10 space-y-6"
    >
      <motion.div
        initial={{ scale: 0.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{
          type: "spring",
          stiffness: 320,
          damping: 22,
          delay: 0.1,
        }}
        className="relative mx-auto size-20 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 grid place-items-center shadow-2xl shadow-emerald-600/35"
      >
        <motion.span
          className="absolute -inset-1 rounded-full ring-4 ring-emerald-500/30"
          initial={{ opacity: 0, scale: 0.85 }}
          animate={{ opacity: [0, 0.7, 0], scale: [0.85, 1.15, 1.25] }}
          transition={{ duration: 0.9, delay: 0.18, ease: "easeOut" }}
          aria-hidden
        />
        <svg
          viewBox="0 0 28 28"
          className="size-10 relative"
          fill="none"
          aria-hidden
        >
          <motion.path
            d="M6 14l5 5 11-11"
            stroke="white"
            strokeWidth={3.2}
            strokeLinecap="round"
            strokeLinejoin="round"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 1 }}
            transition={{
              pathLength: { duration: 0.5, delay: 0.3, ease: "easeOut" },
              opacity: { duration: 0.1, delay: 0.3 },
            }}
          />
        </svg>
      </motion.div>
      <div className="space-y-2">
        <h2 className="text-[26px] md:text-[32px] font-bold text-zinc-900 tracking-tight leading-tight">
          {QE_SEND_COPY.successTitle}
        </h2>
        <p className="text-[14px] text-zinc-600 leading-relaxed max-w-md mx-auto">
          {subtitle}
        </p>
        <p className="text-[13px] text-zinc-400 leading-relaxed">
          {QE_SEND_COPY.successEta}
        </p>
      </div>
      {earningsLine && (
        <motion.p
          initial={{ opacity: 0, scale: 0.85, y: 6 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{
            type: "spring",
            stiffness: 380,
            damping: 24,
            delay: 0.45,
          }}
          className="inline-flex items-center px-3.5 py-1.5 rounded-full bg-emerald-50 ring-1 ring-emerald-200 text-[13.5px] font-bold text-emerald-700 tabular-nums"
        >
          {earningsLine}
        </motion.p>
      )}
      {vehicle && (
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-zinc-50 ring-1 ring-zinc-200">
          {vehicle.colorHex && (
            <span
              className="size-3 rounded-full ring-1 ring-zinc-200/80"
              style={{ background: vehicle.colorHex }}
              aria-hidden
            />
          )}
          <span className="text-[12.5px] font-semibold text-zinc-700">
            {[vehicle.year, vehicle.make, vehicle.model]
              .filter(Boolean)
              .join(" ") || "Vehicle"}
          </span>
        </div>
      )}
      {partial && (
        <div className="rounded-xl border border-amber-200 bg-amber-50/60 px-4 py-3 mx-auto max-w-md text-left flex items-start gap-2.5">
          <AlertCircle
            className="size-4 text-amber-600 shrink-0 mt-0.5"
            aria-hidden
          />
          <p className="text-[12.5px] font-medium text-amber-900 leading-snug">
            {QE_SEND_COPY.partialAttached(photosAttached, totalPhotos)}
          </p>
        </div>
      )}
      <div className="flex flex-col gap-2 max-w-sm mx-auto pt-1">
        <PrimaryButton onClick={onPrimary} type="button" size="wide">
          {QE_SEND_COPY.successPrimary}
        </PrimaryButton>
        <button
          type="button"
          onClick={onSecondary}
          className="w-full h-12 rounded-xl border border-zinc-200 bg-white hover:bg-zinc-50 text-[14px] font-semibold text-zinc-700 transition-colors duration-150"
        >
          {QE_SEND_COPY.successSecondary}
        </button>
      </div>
    </motion.div>
  )
}
